import { Component as BaseComponent, h } from 'preact';
import { connect } from 'react-redux';
import { isValidEmail } from '../../../shared/util';
import { MediaQuery as MQ } from '../../../shared/media-query';
import { Suspense, lazy } from 'preact/compat';
const InputGroup = lazy(
    () => import(/* webpackChunkName: "input-group" */ '../../../shared/form/input-group')
);

class Component extends BaseComponent {
    constructor(props) {
        super(props);

        this.devcat = MQ.isMobile() ? 'Mobile' : 'Desktop';

        if (props.user && props.user.logged_in) {
            this.state = {
                email: {
                    value: props.user && props.user.email,
                    error: null,
                },
                password: {
                    value: null,
                    error: null,
                },
            };
        }
    }

    componentDidMount() {
        // Sync passwordError if it's already set when component mounts
        if (this.props.passwordError) {
            this.setState({
                password: {
                    value: this.state.password.value,
                    error: this.props.passwordError,
                },
            });
        }

        this.initializeGoogleSignIn();

        this.initializeAppleSignIn();
    }

    componentDidUpdate(prevProps) {
        // Sync passwordError from props to local state when it appears
        if (this.props.passwordError && !prevProps.passwordError) {
            this.setState({
                password: {
                    value: this.state.password.value,
                    error: this.props.passwordError,
                },
            });
        }

        // Sync email from props to local state if it changes
        if (this.props.user && this.props.user.email &&
            this.state.email.value !== this.props.user.email) {
            this.setState({
                email: {
                    value: this.props.user.email,
                    error: null,
                },
            });
        }
    }

    componentWillUnmount() {
        this.props.userUpdate({
            emailError: null,
            passwordError: null,
        });
    }

    initializeGoogleSignIn = () => {
        const maxRetries = 3;
        const retryDelay = 500; // 500ms
        let retryCount = 0;

        const tryInitializeGoogle = () => {
            if (window.google && window.google.accounts && window.google.accounts.id) {
                window.google.accounts.id.initialize({
                    client_id: this.props.google && this.props.google.client_id,
                    callback: this.googleSignin,
                    auto_select: false,
                    context: 'signin',
                    ux_mode: 'popup',
                });

                window.google.accounts.id.renderButton(
                    document.getElementById('google-signin-button'),
                    {
                        type: 'standard',
                        theme: 'outline',
                        size: 'large',
                        text: 'signin',
                        shape: 'rectangular',
                        logo_alignment: 'left',
                        width: '100',
                    }
                );
            } else if (retryCount < maxRetries) {
                retryCount++;
                setTimeout(tryInitializeGoogle, retryDelay);
            }
        };

        tryInitializeGoogle();
    }

    initializeAppleSignIn = () => {
        if (window.AppleID && window.AppleID.auth) {
            window.AppleID.auth.init({
                clientId: this.props.apple && this.props.apple.client_id,
                scope: 'name email',
                redirectURI: window.location.origin,
                usePopup: true,
            });
        }
    }

    render({ passwordError, user, close }, { email, password }) {
        if (user && user.logged_in) {
            return (
                <login-modal>
                    <row className='has-padding-15'>
                        <column className='is-2-tablet is-hidden-mobile is-hidden-desktop' />
                        <column className='is-12-mobile is-8-tablet is-5'>
                            <dialog-header>
                                <row className='has-margin-15-bottom'>
                                    <column className='is-6 login-logo'>
                                        <img
                                            src='https://gfx.tarot.com/images/navigation/icons/navigation-icons-newlogo-full.svg' // eslint-disable-line max-len
                                            alt='Tarot.com'
                                        />
                                    </column>
                                    <button className='close-icon' onClick={close} />
                                </row>
                                <h1 className='fancy-title has-text-center'>
                                    Welcome back, {user.username}
                                </h1>
                                <p className='has-text-center has-margin-0-bottom'>
                                    Please enter your password to continue:
                                </p>
                            </dialog-header>
                            <dialog-content>
                                <form autoComplete='on' onSubmit={this.validate}>
                                    <row>
                                        <column className='is-12'>
                                            <Suspense fallback={<div className='skeleton' />}>
                                                <InputGroup
                                                    disabled
                                                    type='text'
                                                    name='email'
                                                    label='Your Email'
                                                    value={email.value}
                                                    error={email.error}
                                                />
                                            </Suspense>
                                        </column>
                                    </row>
                                    <row className='has-margin-20-bottom'>
                                        <column className='is-12'>
                                            <Suspense fallback={<div className='skeleton' />}>
                                                <InputGroup
                                                    autoFocus
                                                    type='password-placeholder'
                                                    name='password'
                                                    placeholder='Password'
                                                    value={password.value}
                                                    error={password.error || passwordError}
                                                    onInput={this.setFormValues}
                                                />
                                            </Suspense>
                                        </column>
                                    </row>
                                    <row className='has-margin-25-bottom'>
                                        <column className='is-12 has-text-center'>
                                            <button
                                                className='next-button'
                                                onClick={this.validate}
                                            >
                                                ENTER
                                            </button>
                                        </column>
                                    </row>
                                    <row>
                                        <column className='is-12'>
                                            <a
                                                href='/password-reset'
                                                target='_blank'
                                                onClick={() => this.trackClick(
                                                    'Modal_AutoLogIn:AccountSettings-ForgotYourPassword'
                                                )}
                                                className='forgot-password'
                                            >
                                                Forgot Your Password?
                                            </a>
                                        </column>
                                    </row>
                                    <row>
                                        <column className='is-12'>
                                            <div className='separator'>
                                                <span>or continue with</span>
                                            </div>
                                        </column>
                                    </row>
                                    <row>
                                        <column className='is-12'>
                                            <div className='sso-buttons'>
                                                <div id='google-signin-button' />
                                                <button
                                                    type='button'
                                                    className='sso-button facebook'
                                                    onClick={this.handleFacebookLogin}
                                                >
                                                    <span className='sso-icon' />
                                                    Facebook
                                                </button>
                                                <button
                                                    type='button'
                                                    className='sso-button apple'
                                                    onClick={this.handleAppleLogin}
                                                >
                                                    <span className='sso-icon' />
                                                    Apple
                                                </button>
                                            </div>
                                        </column>
                                    </row>
                                    <row className='is-aligned-center has-margin-15-top'>
                                        <column className='is-12 has-text-center'>
                                            <p
                                                className='has-margin-0-bottom has-text-size-11 has-line-height-14'
                                            >
                                                Not You?
                                            </p>
                                            <a
                                                href='#'
                                                onClick={this.logoutHomeLogin}
                                                className='has-text-size-11 has-line-height-14'
                                            >
                                                Click here to log in
                                            </a>
                                        </column>
                                    </row>
                                </form>
                            </dialog-content>
                        </column>
                        <column className='is-hidden-mobile is-hidden-tablet is-7'>
                            <div className='signup-promo'>
                                <div className='video-container'>
                                    <video
                                        autoPlay
                                        muted
                                        loop
                                        playsInline
                                        poster='https://gfx.tarot.com/images/signup/tarot-reader-fallback.webp'
                                    >
                                        <source
                                            src='https://gfx.tarot.com/images/signup/tarot-reader-vid.mp4'
                                            type='video/mp4'
                                        />
                                    </video>
                                </div>
                            </div>
                        </column>
                    </row>
                </login-modal>
            );
        }

        return null;
    }

    setFormValues = (e) => {
        let state = {};

        if ((e.type == 'keyup' && e.key == 'Enter') || e.type == 'change' || e.type == 'input') {
            state[e.target.name] = {
                value: e.target.value,
                error: null,
            };

            this.setState(state);
        }
    }

    validate = (e) => {
        e.preventDefault();
        this.trackClick('Modal_AutoLogIn:AccountSettings-EnterPW');

        this.props.userUpdate({
            passwordError: null,
        });

        const state = {
            email: {
                value: this.state.email.value,
                error: isValidEmail(this.state.email.value) ? null : 'Please enter a valid email',
            },
            password: {
                value: this.state.password.value,
                error: this.state.password.value ? this.validatePassword() : 'Please enter a password',
            },
        };

        this.setState(state);

        if (
            !state.email.error
            &&
            !state.password.error
        ) {
            this.props.userLoginViaPassword({
                email: this.state.email.value,
                password: this.state.password.value,
            });
        }
    }

    validatePassword = () => {
        return this.state.password.value.length > 3 ? null : 'Please enter a password longer than 3 characters.';
    }

    trackClick = (info) => {
        this.props.trackEvent(['Click', info]);
    }

    logoutHomeLogin = (e) => {
        e.preventDefault();
        this.trackClick('Modal_AutoLogIn:AccountSettings-NotYou');
        this.props.userLogoutHomeLogin();
    }

    validateSsoEmail = (ssoEmail) => {
        const prefilledEmail = this.state.email.value || this.props.user.email;
        if (!prefilledEmail || !ssoEmail) {
            return false;
        }
        // Compare emails case-insensitively
        return prefilledEmail.toLowerCase().trim() === ssoEmail.toLowerCase().trim();
    }

    handleFacebookLogin = () => {
        if (window.FB) {
            window.FB.login((response) => {
                if (response.authResponse) {
                    window.FB.api(
                        '/me',
                        { fields: 'name,email' },
                        (profileResponse) => {
                            if (profileResponse && !profileResponse.error) {
                                if (!this.validateSsoEmail(profileResponse.email)) {
                                    this.setState({
                                        password: {
                                            value: this.state.password.value,
                                            error: 'The email associated with this Facebook account does not match ' +
                                                'your account email.',
                                        },
                                    });
                                    return;
                                }

                                this.props.userLoginViaPassword({
                                    sso: 'facebook',
                                    credential: response.authResponse.accessToken,
                                    user_id: response.authResponse.userID,
                                    email: profileResponse.email,
                                });
                            }
                        }
                    );
                }
            }, { scope: 'public_profile,email' });
        }
    }

    googleSignin = (response) => {
        const responsePayload = this.decodeJwtResponse(response.credential);
        const email = responsePayload.email;

        if (!this.validateSsoEmail(email)) {
            this.setState({
                password: {
                    value: this.state.password.value,
                    error: 'The email associated with this Google account does not match your account email.',
                },
            });
            return;
        }

        this.props.userLoginViaPassword({
            credential: response.credential,
            sso: 'google',
        });
    }

    decodeJwtResponse = (token) => {
        let base64Url = token.split('.')[1];
        let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        let jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));

        return JSON.parse(jsonPayload);
    }

    handleAppleLogin = () => {
        if (window.AppleID && window.AppleID.auth) {
            window.AppleID.auth.signIn()
                .then((response) => {
                    if (response && response.authorization) {
                        const responsePayload = this.decodeJwtResponse(response.authorization.id_token);
                        const email = responsePayload.email;

                        if (!this.validateSsoEmail(email)) {
                            this.setState({
                                password: {
                                    value: this.state.password.value,
                                    error: 'The email associated with this Apple account does not match ' +
                                        'your account email.',
                                },
                            });
                            return;
                        }

                        this.props.userLoginViaPassword({
                            sso: 'apple',
                            credential: response.authorization.id_token,
                            email: email,
                        });
                    }
                })
                .catch(() => {
                    // Error handling for Apple login
                });
        }
    }
}

const mapStateToProps = (state) => ({
    route: state.route,
    product: state.product,
    user: state.user,
    mailListType: state['mail-lists'] && state['mail-lists'].type,
    emailError: state.user && state.user.emailError,
    passwordError: state.user && typeof state.user.passwordError === 'boolean'
        ? 'Password did not match this email'
        : state.user.passwordError,
    google: state.google,
    apple: state.apple,
});

const mapDispatchToProps = (dispatch) => ({
    trackEvent: (payload) => dispatch({ type: 'ga:event', payload: payload }),
    trackCustomEvent: (payload) => dispatch({ type: 'ga:custom_event', payload: payload }),
    userLoginViaPassword: (payload) => dispatch({ type: 'user:login-via-password', payload: payload }),
    userUpdate: (payload) => dispatch({ type: 'user:update', payload: payload }),
    userLogoutHomeLogin: () => dispatch({ type: 'user:logout-home-login' }),
});

export default connect(mapStateToProps, mapDispatchToProps)(Component);
